import {
    Component,
    forwardRef,
    HostBinding,
    HostListener,
    input,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';

/**
 * Interruptor (toggle) booleano, integrável a formulários via
 * `ControlValueAccessor`.
 *
 * Pode ser alternado por clique ou pelas teclas Enter/Espaço (o host é
 * focável, `tabindex=0`). Estilos encapsulados (`ViewEncapsulation.Emulated`).
 */
@Component({
    selector: 'ai-switch',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => Switch),
            multi: true,
        },
    ],
    imports: [ReactiveFormsModule, FormsModule],
    templateUrl: './switch.component.html',
    styleUrls: ['./switch.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
})
export class Switch implements ControlValueAccessor {
    @HostBinding('attr.tabindex')
    tabindex = 0;

    /** Identificador usado para associar `<input>` e `<label>` (obrigatório). */
    readonly id = input.required<string>();
    protected readonly formControl = new FormControl(false);

    constructor() {
        // takeUntilDestroyed sem argumento usa o DestroyRef do contexto de
        // injeção (construtor), encerrando a assinatura com o componente.
        this.formControl.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe((value: boolean | null) => {
                this.onChanged(value === true);
            });
    }

    onChanged: (value: boolean) => void = () => {};
    onTouched: () => void = () => {};

    writeValue(value: boolean): void {
        this.formControl.setValue(value, { emitEvent: false });
    }

    registerOnChange(fn: any): void {
        this.onChanged = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    toggleSwitch(): void {
        this.formControl.setValue(!this.formControl.value, { emitEvent: true });
        this.onChanged(this.formControl.value || false);
        this.onTouched();
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.formControl.disable({ emitEvent: false });
        } else {
            this.formControl.enable({ emitEvent: false });
        }
    }

    @HostListener('keyup', ['$event'])
    onKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter' || event.key === ' ') {
            if (!this.formControl.disabled) {
                this.toggleSwitch();
            }
        }
    }
}
